<template>
  <v-text-field
    v-bind="$attrs"
    :model-value="displayValue"
    @input="handleInput"
    @keydown="handleKeydown"
    @blur="handleBlur"
    @focus="handleFocus"
    @paste="handlePaste"
    :rules="validationRules"
    :placeholder="'+7 (700) 123-45-67'"
    :prepend-inner-icon="prependIcon"
    variant="outlined"
    inputmode="tel"
    autocomplete="tel"
    type="tel"
  >
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </v-text-field>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'

const PREFIX = '+7'

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Телефон нөмірі'
  },
  placeholder: {
    type: String,
    default: '+7 (700) 123-45-67'
  },
  required: {
    type: Boolean,
    default: false
  },
  countryCode: {
    type: String,
    default: '+7'
  },
  prependIcon: {
    type: String,
    default: 'mdi-phone'
  }
})

// Emits
const emit = defineEmits(['update:modelValue'])

// Internal digits after +7 (max 10 digits)
const digits = ref('')

// Initialize from modelValue
const extractDigits = (value) => {
  if (!value) return ''
  const cleaned = value.replace(/\D/g, '')
  // If starts with 7 or 8, skip first digit
  if (cleaned.startsWith('7') || cleaned.startsWith('8')) {
    return cleaned.slice(1, 11)
  }
  return cleaned.slice(0, 10)
}

digits.value = extractDigits(props.modelValue)

// Format digits into display: +7 (xxx) xxx-xx-xx
const formatDisplay = (d) => {
  if (!d) return PREFIX
  let result = PREFIX
  if (d.length > 0) result += ' (' + d.slice(0, 3)
  if (d.length >= 3) result += ') '
  if (d.length > 3) result += d.slice(3, 6)
  if (d.length > 6) result += '-' + d.slice(6, 8)
  if (d.length > 8) result += '-' + d.slice(8, 10)
  return result
}

// Get raw phone number for emit: +7XXXXXXXXXX
const getRawPhone = (d) => {
  if (!d) return ''
  return '+7' + d
}

const displayValue = computed(() => formatDisplay(digits.value))

// Emit current value
const emitValue = () => {
  emit('update:modelValue', formatDisplay(digits.value))
}

// Block keys: only allow digits, Backspace, Delete, arrows, Tab
const handleKeydown = (event) => {
  const { key, ctrlKey, metaKey } = event
  // Allow control combinations (Ctrl+C, Ctrl+V, Ctrl+A, etc.)
  if (ctrlKey || metaKey) return
  // Allow navigation keys
  if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(key)) {
    // Prevent deleting the +7 prefix
    if (key === 'Backspace' || key === 'Delete') {
      const input = event.target
      const selStart = input.selectionStart
      const selEnd = input.selectionEnd
      // If cursor is within or would delete into "+7 " prefix area and no digits remain
      if (key === 'Backspace' && selStart <= 2 && selStart === selEnd) {
        event.preventDefault()
        return
      }
    }
    return
  }
  // Only allow digit keys
  if (!/^\d$/.test(key)) {
    event.preventDefault()
    return
  }
  // Max 10 digits
  if (digits.value.length >= 10) {
    event.preventDefault()
    return
  }
}

// Handle input event — re-extract digits from whatever the input shows
const handleInput = (event) => {
  const raw = event.target.value || ''
  const allDigits = raw.replace(/\D/g, '')
  
  // Extract digits after the country code "7"
  let newDigits = ''
  if (allDigits.startsWith('7') || allDigits.startsWith('8')) {
    newDigits = allDigits.slice(1, 11)
  } else {
    newDigits = allDigits.slice(0, 10)
  }
  
  digits.value = newDigits
  
  // Force the display to be correctly formatted
  nextTick(() => {
    const input = event.target
    if (input) {
      const formatted = formatDisplay(newDigits)
      input.value = formatted
      // Place cursor at the end
      const pos = formatted.length
      input.setSelectionRange(pos, pos)
    }
  })
  
  emitValue()
}

// Handle paste — extract only digits
const handlePaste = (event) => {
  event.preventDefault()
  const pasted = (event.clipboardData || window.clipboardData).getData('text') || ''
  const pastedDigits = pasted.replace(/\D/g, '')
  
  let newDigits = ''
  if (pastedDigits.startsWith('7') || pastedDigits.startsWith('8')) {
    newDigits = pastedDigits.slice(1, 11)
  } else {
    newDigits = pastedDigits.slice(0, 10)
  }
  
  // Append to existing or replace
  const input = event.target
  const selStart = input.selectionStart
  
  // If user selected all — replace all digits
  if (input.selectionStart <= 2 && input.selectionEnd >= input.value.length) {
    digits.value = newDigits
  } else {
    // Append to existing digits (up to 10 total)
    digits.value = (digits.value + newDigits).slice(0, 10)
  }
  
  nextTick(() => {
    const formatted = formatDisplay(digits.value)
    input.value = formatted
    const pos = formatted.length
    input.setSelectionRange(pos, pos)
  })
  
  emitValue()
}

// On focus, ensure +7 prefix is there and cursor is after it
const handleFocus = () => {
  // Don't manipulate DOM directly during focus - breaks iOS PWA keyboard
  if (!digits.value) {
    digits.value = ''
  }
  emitValue()
}

// On blur, finalize formatting
const handleBlur = () => {
  emitValue()
}

// Validation rules
const validationRules = computed(() => {
  const rules = []
  
  if (props.required) {
    rules.push((v) => (!!v && v !== PREFIX) || 'Бұл өріс міндетті')
  }
  
  rules.push((v) => {
    if (!v || v === PREFIX) return true
    const phoneRegex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/
    if (!phoneRegex.test(v)) {
      return 'Телефон нөмірін дұрыс форматта енгізіңіз: +7 (700) 123-45-67'
    }
    return true
  })
  
  return rules
})

// Watch model value changes from parent
watch(() => props.modelValue, (newValue) => {
  const newDigits = extractDigits(newValue)
  if (newDigits !== digits.value) {
    digits.value = newDigits
  }
})
</script>